import { useState } from "react";
import {
  MAX_TABLE_LABEL,
  MAX_TABLE_SEATS,
  MIN_TABLE_SEATS,
  renameTable,
  resizeTable,
} from "@/lib/store";
import type { TableConfig } from "@/lib/types";

/**
 * 改桌號與每桌人數：把「1、2、3」換成餐廳實際的叫法（主桌、A、梅花廳…），
 * 並在餐廳臨時改桌型時調整座位數。
 *
 * 座位代號 = 桌號 + 字母，所以這兩件事都不只是改顯示文字：
 *   - 改桌號時，後端會在同一個交易裡把坐在這桌的人一起換過去（2G → 主桌G）。
 *   - 改座位數時，加座位永遠安全；減座位等於把後面幾個字母的位子拿掉，
 *     所以只有那些位子沒人坐時才會過，否則後端會回報是誰坐著。
 *
 * 一次只改一桌，每一次都是一個完整、可回溯的交易。
 */
export function TableNames({
  code,
  tables,
  onRenamed,
}: {
  code: string;
  tables: TableConfig[];
  /** 改名成功後通知父層（平面配置編輯器要把座標的 key 一起換掉） */
  onRenamed?: (from: string, to: string) => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [seats, setSeats] = useState(MIN_TABLE_SEATS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const open = (t: TableConfig) => {
    setEditing(t.label);
    setDraft(t.label);
    setSeats(t.seats);
    setError(null);
    setSaved(null);
  };

  const close = () => {
    setEditing(null);
    setDraft("");
    setError(null);
  };

  const submit = async (from: string, wasSeats: number) => {
    const nextLabel = draft.trim();
    const nextSeats = Math.max(MIN_TABLE_SEATS, Math.min(MAX_TABLE_SEATS, Math.round(seats) || wasSeats));
    if ((!nextLabel || nextLabel === from) && nextSeats === wasSeats) {
      close();
      return;
    }
    setBusy(true);
    setError(null);

    // 先改座位數再改桌號：座位數是會被規則擋下來的那個，
    // 擋下來時就整個中止，主辦人看到的是「什麼都沒改」而不是改了一半。
    if (nextSeats !== wasSeats) {
      const r = await resizeTable(code, from, nextSeats);
      if (!r.ok) {
        setBusy(false);
        setError(resizeError(r, from, nextSeats));
        return;
      }
    }

    if (nextLabel && nextLabel !== from) {
      const r = await renameTable(code, from, nextLabel);
      if (!r.ok) {
        setBusy(false);
        setError(
          (nextSeats !== wasSeats ? `座位數已改成 ${nextSeats} 位，但桌號沒改成功：` : "") +
            (r.error === "duplicate"
              ? "已經有一桌叫這個名字了，換一個。"
              : r.error === "invalid"
                ? `桌號要 1–${MAX_TABLE_LABEL} 個字，而且不能有空白。`
                : "改名失敗，請再試一次。"),
        );
        return;
      }
      onRenamed?.(from, r.label);
      setBusy(false);
      setSaved(`${r.label}（${nextSeats} 位）`);
      close();
      return;
    }

    setBusy(false);
    setSaved(`${from}（${nextSeats} 位）`);
    close();
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {tables.map((t) =>
          editing === t.label ? (
            <span key={t.label} className="border-line flex flex-wrap items-center gap-1.5 rounded-xl border px-2 py-1.5">
              <input
                value={draft}
                autoFocus
                maxLength={MAX_TABLE_LABEL}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submit(t.label, t.seats);
                  if (e.key === "Escape") close();
                }}
                className="field-input w-24 py-1.5 text-center text-[14px] font-bold"
                aria-label={`第 ${t.label} 桌的桌號`}
              />
              <input
                type="number"
                value={seats}
                min={MIN_TABLE_SEATS}
                max={MAX_TABLE_SEATS}
                onChange={(e) => setSeats(Number(e.target.value))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submit(t.label, t.seats);
                  if (e.key === "Escape") close();
                }}
                className="field-input w-16 px-2 py-1.5 text-center text-[14px]"
                aria-label={`第 ${t.label} 桌的座位數`}
              />
              <span className="text-sub text-[13px]">位</span>
              <button
                onClick={() => submit(t.label, t.seats)}
                disabled={busy}
                className="btn btn-primary px-3 py-1.5 text-[13px]"
              >
                {busy ? "…" : "儲存"}
              </button>
              <button
                onClick={close}
                disabled={busy}
                className="btn btn-secondary px-3 py-1.5 text-[13px]"
              >
                取消
              </button>
            </span>
          ) : (
            <button
              key={t.label}
              onClick={() => open(t)}
              title="點一下改桌號與人數"
              className={`btn btn-secondary px-3 py-1.5 text-[13px] ${t.vip ? "text-gold" : ""}`}
            >
              <span className="font-bold">{t.label}</span>
              <span className="text-sub ml-1.5 font-normal">{t.seats} 位</span>
            </button>
          ),
        )}
      </div>

      {error && <p className="text-bad-deep mt-2 text-[13px] leading-relaxed">{error}</p>}
      {saved && !error && (
        <p className="text-ok-deep mt-2 text-[13px]">✓ 已改成「{saved}」</p>
      )}

      <p className="text-sub mt-2 text-[12px] leading-relaxed">
        點桌子就能改桌號與人數。桌號最多 {MAX_TABLE_LABEL} 個字、不能重複也不能有空白；
        座位代號是「桌號 + 字母」，所以第 2 桌改叫「主桌」之後 2G 會變成主桌G，已選好位子的人不用重選。
        加人數隨時可以；減人數等於把後面幾個位子收掉，只有那些位子還沒人坐時才改得動。
      </p>
    </div>
  );
}

/** 把後端擋下來的原因翻成主辦人看得懂、而且知道下一步要做什麼的句子 */
function resizeError(
  r: Exclude<Awaited<ReturnType<typeof resizeTable>>, { ok: true }>,
  label: string,
  seats: number,
): string {
  if (r.error === "occupied") {
    const who = r.occupied.map((o) => `${o.seat} 的${o.name}`).join("、");
    return `改不了：${who} 還坐在第 ${label} 桌要收掉的位子上。請他們改選其他位子之後再縮。`;
  }
  if (r.error === "below-confirmed") {
    return `改不了：已經有 ${r.confirmed} 人確認報名，第 ${label} 桌縮成 ${seats} 位之後全場只剩 ${r.capacity} 席。先請人取消報名，或把其他桌加大。`;
  }
  return "改人數失敗，請再試一次。";
}
